import { supabase } from '../supabase';

export type FruticReplacement = { id: string; reference: string };

export async function synchronizeFruticShipments(
  exporterId: number,
  season: string,
  speciesId: number,
  action: 'sync' | 'accept_replacement' = 'sync',
  caseId?: string
): Promise<FruticReplacement[]> {
  const { data, error } = await supabase.functions.invoke(
    'sync-frutic-shipments',
    {
      body: {
        exporter_id: exporterId,
        season,
        species_id: speciesId,
        action,
        case_id: caseId,
      },
    }
  );
  if (error) {
    const detail = error.context instanceof Response
      ? await error.context.json().catch(() => null)
      : null;
    throw new Error(detail?.error || error.message);
  }
  if (!data?.success)
    throw new Error(
      data?.error || error?.message || 'No se pudieron actualizar los despachos'
    );
  return data.replacements || [];
}
import { VALID_FORMATS } from '../utils/definitions';
import { BoxFormatMapping, ClientRankingsExporters } from '../utils/types';
import axios from 'axios';

export async function getFruticBoxToFormatMapping(
  userProfile
): Promise<[] | null> {
  try {
    const { data, error } = await supabase
      .from('frutic_box_to_format')
      .select('*')
      .eq('exporter_id', userProfile?.exporter_id);

    return data;
  } catch (error) {
    console.error('Error getting frutic box to format mapping:', error);
    return null;
  }
}

export async function saveFruticBoxToFormatMapping(
  userProfile,
  fruticCode: string,
  format: (typeof VALID_FORMATS)[number]
): Promise<BoxFormatMapping | null> {
  try {
    const newMapping = {
      exporter_id: userProfile?.exporter_id,
      frutic_code: fruticCode,
      cherry_box_format: format,
    };

    // Check if mapping already exists
    const { data: existingMapping, error: checkError } = await supabase
      .from('frutic_box_to_format')
      .select('*')
      .eq('exporter_id', userProfile?.exporter_id)
      .eq('frutic_code', fruticCode)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      throw checkError;
    }

    // If mapping exists, update it instead of inserting
    if (existingMapping) {
      const updatePayload: {
        cherry_box_format: (typeof VALID_FORMATS)[number];
      } = {
        cherry_box_format: format,
      };

      const { data, error } = await supabase
        .from('frutic_box_to_format')
        .update(updatePayload)
        .eq('id', existingMapping.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    const { data, error } = await supabase
      .from('frutic_box_to_format')
      .insert(newMapping)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error getting frutic box to format mapping:', error);
    return null;
  }
}

export async function getShipmentBoxingsFromFrutic(
  speciesId: number,
  season: string,
  session: { access_token: string } | null,
  extraQuery: { withReceptionDate: boolean; withProcessDate: boolean } = {
    withReceptionDate: false,
    withProcessDate: false,
  },
  exporterRef?: string,
  containerNumber?: string
): Promise<[] | null> {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }
    //  console.log("WILL CALL FRUTIC");

    const response = await axios.post(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-frutic-shipments`,
      {
        season,
        speciesId,
        withReceptionDate: extraQuery.withReceptionDate,
        withProcessDate: extraQuery.withProcessDate,
        exporterRef: exporterRef,
        containerNumber: containerNumber,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting shipments:', error);
    return null;
  }
}

export async function getShipmentBoxingsTypesFromFrutic(
  speciesId: number,
  season: string,
  session: { access_token: string } | null,
  exporterRef?: string
): Promise<[] | null> {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }
    const response = await axios.post(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-frutic-shipment-boxing-types`,
      {
        season,
        speciesId,
        exporterRef,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting shipments:', error);
    return null;
  }
}

export async function getShipmentsFromFrutic(
  speciesId: number,
  season: string,
  session: { access_token: string } | null
): Promise<[] | null> {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await axios.post(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-frutic-shipments-summary`,
      {
        season,
        speciesId,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting shipments:', error);
    return null;
  }
}

export async function getMovementsFromFrutic(
  speciesId: number,
  season: string,
  session: { access_token: string } | null
): Promise<[] | null> {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await axios.post(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-frutic-movements`,
      {
        season,
        speciesId,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting shipments:', error);
    return null;
  }
}

// Receiver mapping functions
export async function getFruticReceiverMapping(
  userProfile
): Promise<[] | null> {
  try {
    const { data, error } = await supabase
      .from('frutic_receiver_to_exporter_receiver')
      .select('*, exporter_receivers(*)')
      .eq('exporter_id', userProfile?.exporter_id);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting frutic receiver mapping:', error);
    return null;
  }
}
